import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import ProcedurePlaceholder from "@/components/ProcedurePlaceholder";
import { HERO } from "@/data/procedureHeroImages";
import PatientTestimonials from "../components/PatientTestimonials";
import SEO from "@/components/SEO";
import { WHATSAPP_URL, TEL_URL, PHONE_DISPLAY } from "@/constants/contact";
import {
  ArrowBigDown,
  ArrowDownRight,
  ArrowRight,
  Award,
  BedDouble,
  Camera,
  ChevronRight,
  CircleDot,
  Clock,
  Droplets,
  Dumbbell,
  GraduationCap,
  Heart,
  Layers,
  MessageCircle,
  Phone,
  Minus,
  Monitor,
  Plus,
  Shuffle,
  Sparkles,
} from "lucide-react";


const Breadcrumb = () => {
  const items = [
    { name: "Home", href: "/" },
    { name: "Cosmetic Surgery", href: "/#services" },
    { name: "Face", href: "/#services" },
    { name: "Cheek Augmentation", href: "/cheek-augmentation-in-mumbai" },
  ];

  return (
    <nav className="bg-warm-white pt-24 pb-4" data-testid="breadcrumb">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <ol className="flex flex-wrap items-center gap-2 text-sm text-deep-navy/60">
          {items.map((item, index) => (
            <li key={index} className="flex items-center gap-2">
              {index > 0 && <ChevronRight className="w-3 h-3 text-gold" />}
              {index === items.length - 1 ? (
                <span className="text-deep-navy/70">{item.name}</span>
              ) : (
                <Link to={item.href} className="hover:text-gold transition-colors">
                  {item.name}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
};

const HeroSection = () => (
  <section className="bg-warm-white pt-8 md:pt-10 pb-12 md:pb-20" data-testid="procedure-hero">
    <div className="container mx-auto px-4 md:px-8 max-w-7xl">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div>
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Cosmetic Surgery · Face</p>
          <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-premium-purple leading-[1.08] mb-5">
            Cheek Augmentation
          </h1>
          <div className="w-12 h-[2px] bg-gold mb-5" />
          <p className="text-lg text-deep-navy/70 mb-8">
            Cheek Implants, Fillers &amp; Fat Grafting by Dr. Vicky Jain
          </p>
          <div className="flex flex-wrap gap-3">
            <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-medium text-premium-purple">Board Certified</span>
            <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-medium text-premium-purple">17+ Years Experience</span>
            <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-medium text-premium-purple">International Training</span>
          </div>
        </div>
        <div className="flex justify-center lg:justify-end">
          <ProcedurePlaceholder procedureName="Cheek Augmentation" icon="◆" size="hero"  imageSrc={HERO.cheekAugmentation} imageAlt="Cheek augmentation" />
        </div>
      </div>
    </div>
  </section>
);

const WhatIsSection = () => (
  <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="what-is-section">
    <div className="container mx-auto px-4 md:px-8 max-w-7xl">
      <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
        <div>
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Overview</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">What is Cheek Augmentation Surgery?</h2>
        </div>
        <div className="space-y-6 text-deep-navy/70 leading-relaxed">
          <p>
            Cheek augmentation enhances cheek volume, improves mid-face contour, and restores the structural support that gives the face a lifted, defined appearance. Well-projected cheekbones are one of the strongest markers of youth and facial symmetry. When that volume is flat or absent, whether from genetics or age-related loss, the entire face reads as tired and older than it is.
          </p>
          <p>
            Dr. Vicky Jain performs cheek augmentation using three approaches: dermal fillers for non-surgical correction, silicone implants for permanent structural enhancement, and fat grafting for patients who prefer using their own tissue. The right method depends on your bone structure, the degree of volume loss, and whether you want a temporary or permanent result.
          </p>
          <p>
            Even a modest increase in cheek projection lifts the nasolabial folds, improves jawline definition, and changes how the whole face reads in photos and in person. It is one of the higher-impact procedures relative to its recovery time.
          </p>
        </div>
      </div>
    </div>
  </section>
);

const DrJainApproachSection = () => (
  <section className="bg-premium-purple py-10 md:py-14" data-testid="dr-jain-approach-section">
    <div className="container mx-auto px-4 md:px-8 max-w-7xl">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div>
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Surgical Approach</p>
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-8">
            Dr. Jain&apos;s Approach to Cheek Augmentation
          </h2>
          <div className="space-y-6 text-white/75 leading-relaxed">
            <p>
              Dr. Jain evaluates the mid-face in three dimensions: bone structure, soft tissue volume, and skin quality. These three factors together determine which technique produces the most natural result. A patient with flat bone structure needs a different approach than one who has lost volume from aging. Treating them the same way produces the wrong outcome.
            </p>
            <p>
              The goal is cheeks that look proportionate to the rest of your face. Overcorrection, placing too much volume in the wrong plane, is what produces the overdone or puffy result patients worry about. Dr. Jain selects filler volume, implant size, or fat graft quantity based on what your specific anatomy needs, not a standard formula.
            </p>
          </div>
        </div>
        <div className="relative">
          <div className="relative overflow-hidden">
            <img
              src="/dr-jain.avif"
              alt="Dr. Vicky Jain"
              loading="lazy"
              decoding="async"
              className="w-full h-[420px] object-cover object-top"
              data-testid="dr-jain-photo"
            />
            <div
              className="absolute top-0 left-0 right-0 h-[420px] pointer-events-none hidden lg:block"
              style={{
                background: "linear-gradient(to right, #3D2566 0%, rgba(61, 37, 102, 0) 15%)",
              }}
            />
          </div>
          <div className="mt-4 text-center lg:text-left">
            <p className="font-serif text-lg text-white/80">Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon</p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const CheekProblemsSection = () => {
  const cards = [
    {
      title: "Flat or Underdeveloped Cheeks",
      icon: Layers,
      copy: "Patients with naturally flat mid-faces lack the cheekbone projection that creates facial definition. Augmentation builds that structure whether through implants or volume addition.",
    },
    {
      title: "Hollow or Sunken Mid-Face",
      icon: ArrowBigDown,
      copy: "Age-related fat loss in the mid-face creates a sunken appearance beneath the eyes and along the cheeks. Fillers or fat grafting restore the volume that has been lost.",
    },
    {
      title: "Tired or Aged Appearance",
      icon: Clock,
      copy: "When cheek volume deflates, the face drops. The skin follows gravity rather than resting on a lifted structure. Restoring cheek projection reverses this without a facelift.",
    },
    {
      title: "Deep Nasolabial Folds",
      icon: ArrowDownRight,
      copy: "Nasolabial folds deepen when mid-face support is absent. Lifting cheek volume reduces the fold by restoring the tissue support above it.",
    },
    {
      title: "Facial Asymmetry",
      icon: Shuffle,
      copy: "Unequal cheek volume or projection on each side creates visible asymmetry. Dr. Jain corrects this by treating each side independently based on what the face needs.",
    },
    {
      title: "Poor Facial Contour in Photos",
      icon: Camera,
      copy: "Flat cheeks photograph poorly. The face reads as two-dimensional without mid-face projection. Augmentation creates the shadow and light contrast that gives the face structure in photos.",
    },
  ];

  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="cheek-problems-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Indications</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">
          What Cheek Augmentation Corrects
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {cards.map((card, index) => (
            <div
              key={index}
              className="bg-white p-8 border border-[#E8E2D9]"
              data-testid={`cheek-problem-card-${index}`}
            >
              <div className="w-12 h-12 bg-gold/10 flex items-center justify-center mb-4" aria-hidden>
                <card.icon className="w-6 h-6 text-gold" />
              </div>
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{card.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{card.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const ProcedureTypesSection = () => {
  const types = [
    {
      title: "Dermal Fillers",
      icon: Droplets,
      copy: "Hyaluronic acid fillers are injected into the mid-face to add volume and lift. The procedure takes 20 to 30 minutes with no anesthesia or minimal topical numbing. Results are immediate. Swelling settles within 3 to 5 days. Results last 9 to 18 months. Fillers can be dissolved if needed, making this the lowest-commitment option. Best for mild to moderate volume loss or patients who want to assess the change before committing to surgery.",
    },
    {
      title: "Cheek Implants",
      icon: CircleDot,
      copy: "Silicone implants are placed through a small incision inside the mouth, leaving no visible external scar. They sit directly on the cheekbone and provide permanent, structured enhancement. Surgery takes 1 to 2 hours under general anesthesia. Best for patients with flat bone structure who want a long-term result that does not require repeat treatments.",
    },
    {
      title: "Fat Grafting",
      icon: Heart,
      copy: "Fat is taken from another area of the body, typically the abdomen or thighs, purified, and injected into the cheeks. Results are long-lasting and use the patient's own tissue. There is a slight unpredictability in how much fat survives the transfer, typically 60 to 80 percent. Best for patients who prefer a natural approach and want to avoid synthetic materials.",
    },
  ];

  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="procedure-types-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Procedure Options</p>
        <h2 className="font-serif text-3xl md:text-4xl text-white mb-10 text-center">
          Types of Cheek Augmentation
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {types.map((item, index) => (
            <div
              key={index}
              className="bg-white p-8 border border-[#E8E2D9]"
              data-testid={`procedure-type-card-${index}`}
            >
              <div className="w-12 h-12 bg-gold/10 flex items-center justify-center mb-4" aria-hidden>
                <item.icon className="w-6 h-6 text-gold" />
              </div>
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{item.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{item.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const HowSurgeryPerformedSection = () => (
  <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="how-surgery-section">
    <div className="container mx-auto px-4 md:px-8 max-w-7xl">
      <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
        <div>
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">The Procedure</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">
            How Cheek Augmentation is Performed
          </h2>
        </div>
        <div className="space-y-6 text-deep-navy/70 leading-relaxed">
          <p>
            Fillers are administered in-clinic in 20 to 30 minutes. No anesthesia is required, though topical numbing is applied. There is no downtime. Most patients return to normal activity the same day.
          </p>
          <p>
            Implant surgery takes 1 to 2 hours under general anesthesia. Dr. Jain places the implant through a small incision inside the mouth along the upper gum line. The implant is positioned directly on the cheekbone and fixed in place. There are no visible external scars.
          </p>
          <p>
            Fat grafting combines a short liposuction procedure to harvest fat with the injection into the cheeks. The total procedure takes 1 to 2 hours. Some overcorrection is done intentionally at the time of surgery to account for the portion of fat that the body reabsorbs during healing.
          </p>
        </div>
      </div>
    </div>
  </section>
);

const RecoveryTimelineSection = () => {
  const milestones = [
    {
      period: "Days 1 to 3",
      description:
        "Mild swelling and bruising, more pronounced with implants and fat grafting. Fillers settle within this window.",
      icon: BedDouble,
    },
    {
      period: "Days 3 to 7",
      description:
        "Fillers patients resume normal activity. Implant patients manage residual swelling. Work is possible for most patients by day 5 to 7.",
      icon: Monitor,
    },
    {
      period: "Week 2",
      description: "Swelling reduces significantly. The cheeks begin to settle into their new shape and feel more natural.",
      icon: Dumbbell,
    },
    {
      period: "1 to 3 Months",
      description:
        "Final results visible. Fat graft survival is established. Implants fully integrate with surrounding tissue.",
      icon: Sparkles,
    },
  ];

  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="recovery-timeline-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <h2 className="font-serif text-3xl md:text-4xl text-white mb-12 text-center">Recovery Timeline</h2>
        <div className="hidden md:block relative">
          <div className="absolute top-16 left-0 right-0 h-0.5 bg-deep-navy/10" />
          <div className="grid grid-cols-4 gap-4">
            {milestones.map((milestone, index) => (
              <div key={index} className="relative" data-testid={`timeline-milestone-${index}`}>
                <div className="flex justify-center mb-3">
                  <milestone.icon className="w-6 h-6 text-deep-navy/40" />
                </div>
                <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center mx-auto mb-4 relative z-10">
                  <div className="w-3 h-3 rounded-full bg-warm-white" />
                </div>
                <div className="text-center">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="md:hidden relative pl-12">
          <div className="absolute top-0 bottom-0 left-4 w-0.5 bg-deep-navy/10" />
          <div className="space-y-8">
            {milestones.map((milestone, index) => (
              <div key={index} className="relative" data-testid={`timeline-mobile-${index}`}>
                <div className="absolute -left-12 top-0 flex flex-col items-center">
                  <milestone.icon className="w-5 h-5 text-deep-navy/40 mb-1" />
                  <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-warm-white" />
                  </div>
                </div>
                <div className="pl-4">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const WhoIsGoodCandidateSection = () => (
  <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="who-is-good-candidate-section">
    <div className="container mx-auto px-4 md:px-8 max-w-7xl">
      <div className="max-w-4xl mx-auto">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Am I a Candidate?</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8">
          Who Is a Good Candidate for Cheek Augmentation?
        </h2>
        <div className="space-y-6 text-deep-navy/70 leading-relaxed">
          <p>
            Good candidates are adults in good general health who have a specific concern with their mid-face: flat cheeks from genetics, volume loss from aging, or facial asymmetry that affects how the face reads at rest and in photos. You do not need significant volume loss to benefit. Patients with subtle flatness often see the most proportionate improvements.
          </p>
          <p>
            Patients considering fillers should have realistic expectations about the temporary nature of results and factor in the cost of repeat treatments over time. Patients considering implants or fat grafting should be non-smokers or willing to stop smoking before surgery, as smoking affects healing. Anyone who has felt their face looks tired, aged, or flat in photos and cannot identify a specific reason is worth assessing for mid-face volume loss.
          </p>
        </div>
      </div>
    </div>
  </section>
);

const PatientResultsSection = () => <PatientTestimonials procedureName="Cheek Augmentation" />;

const FAQSection = () => {
  const faqs = [
    {
      question: "What is the difference between cheek fillers and cheek implants?",
      answer:
        "Fillers add volume using injectable hyaluronic acid and last 9 to 18 months. Implants are surgically placed silicone structures that sit on the cheekbone permanently. Fillers suit mild cases and patients who want flexibility. Implants suit patients with flat bone structure who want a permanent result.",
    },
    {
      question: "Is cheek augmentation painful?",
      answer:
        "Filler injections involve minimal discomfort with topical numbing. Implant surgery is performed under general anesthesia, so there is no pain during the procedure. Post-operative discomfort from implant surgery is mild and managed with medication.",
    },
    {
      question: "How long do cheek filler results last?",
      answer:
        "Hyaluronic acid fillers in the cheeks typically last 9 to 18 months depending on the product used and how your body metabolizes it. Implants and fat grafting produce long-lasting to permanent results.",
    },
    {
      question: "Will cheek augmentation look natural?",
      answer:
        "Yes, when the volume or implant size is matched to your facial proportions. The most common cause of unnatural results is overcorrection: placing too much volume in the wrong anatomical plane.",
    },
    {
      question: "Can cheek augmentation reduce nasolabial folds?",
      answer:
        "Yes. Restoring mid-face volume lifts the tissue above the fold and reduces its depth indirectly. It is not a direct treatment for the fold itself, but mid-face support is one of the main reasons folds deepen with age.",
    },
    {
      question: "Can cheek augmentation be combined with other procedures?",
      answer:
        "Yes. It is commonly combined with chin augmentation for overall facial balance, rhinoplasty for complete facial harmony, and facelift or thread lift for patients with skin laxity alongside volume loss.",
    },
    {
      question: "Are there visible scars after cheek implant surgery?",
      answer: "No. The incision is made inside the mouth along the upper gum line. There are no external scars.",
    },
    {
      question: "Can cheek fillers be reversed?",
      answer:
        "Yes. Hyaluronic acid fillers can be dissolved with hyaluronidase at any point. Implants require a surgical procedure to remove. Fat grafting cannot be reversed once the fat has integrated.",
    },
  ];

  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="faq-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Common Questions</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Frequently Asked Questions</h2>
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="border-b border-deep-navy/10"
                data-testid={`faq-item-${index}`}
              >
                <AccordionTrigger className="text-left font-serif text-lg text-premium-purple hover:no-underline hover:text-gold py-6 [&[data-state=open]>div>svg:first-child]:hidden [&[data-state=open]>div>svg:last-child]:block [&>svg]:hidden">
                  <span className="pr-4">{faq.question}</span>
                  <div className="flex-shrink-0">
                    <Plus className="w-5 h-5 text-gold" />
                    <Minus className="w-5 h-5 text-gold hidden" />
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-deep-navy/70 leading-relaxed pb-6">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
};

const ConsultationCTASection = () => (
  <section className="bg-premium-purple py-10 md:py-14" data-testid="consultation-cta-section">
    <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
      <h2 className="font-serif text-3xl md:text-4xl text-warm-white mb-4">
        Book Your Cheek Augmentation Consultation
      </h2>
      <p className="text-warm-white/70 mb-8 max-w-2xl mx-auto leading-relaxed">
        During your consultation, Dr. Jain assesses your mid-face bone structure, soft tissue volume, and skin quality to determine which technique produces the right result for your face. You leave with a clear recommendation, a realistic picture of outcomes, and a full understanding of what the procedure and recovery involve.
      </p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
        <Button
          asChild
          className="bg-gold text-white hover:bg-gold-hover border-none rounded-none px-8 py-6 text-base"
          data-testid="cta-book-consultation"
        >
          <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
            Book Consultation
            <ArrowRight className="w-5 h-5 ml-2" />
          </a>
        </Button>
        <Button
          asChild
          variant="outline"
          className="border-warm-white text-warm-white hover:bg-warm-white hover:text-deep-navy rounded-none px-8 py-6 text-base"
          data-testid="cta-call"
        >
          <a href={TEL_URL}>
            <Phone className="w-5 h-5 mr-2" />
            Call {PHONE_DISPLAY}
          </a>
        </Button>
      </div>
    </div>
  </section>
);

const RelatedProceduresSection = () => {
  const procedures = [
    {
      name: "Chin Augmentation",
      description: "Balance mid-face and lower face projection together.",
      href: "/chin-augmentation-in-mumbai",
    },
    {
      name: "Facelift",
      description: "Address skin laxity alongside volume restoration for complete facial rejuvenation.",
      href: "/facelift-surgery-mumbai",
    },
    {
      name: "Thread Lift",
      description: "Non-surgical lifting option for patients with early sagging and volume loss.",
      href: "/thread-lift-mumbai",
    },
  ];

  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="related-procedures-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Related Procedures</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Related Procedures</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {procedures.map((procedure, index) => (
            <div
              key={index}
              className="bg-white p-8 border border-[#E8E2D9] hover:shadow-lg transition-shadow duration-300 group"
              data-testid={`related-procedure-${index}`}
            >
              <h3 className="font-serif text-xl text-premium-purple mb-3 font-semibold tracking-tight">{procedure.name}</h3>
              <p className="text-deep-navy/60 mb-6 leading-relaxed">{procedure.description}</p>
              <a
                href={procedure.href}
                className="inline-flex items-center text-deep-navy font-medium hover:text-gold transition-colors"
              >
                Learn More →
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const cheekBreadcrumb = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://drvickyjain.com/" },
    { "@type": "ListItem", position: 2, name: "Cosmetic Surgery", item: "https://drvickyjain.com/cosmetic-surgery-in-mumbai" },
    { "@type": "ListItem", position: 3, name: "Cheek Augmentation", item: "https://drvickyjain.com/cheek-augmentation-in-mumbai" },
  ],
};

const cheekStructuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": ["MedicalProcedure", "SurgicalProcedure"],
      name: "Cheek Augmentation",
      alternateName: ["Cheek implants", "Mid-face augmentation", "Malar augmentation"],
      description: "Restoration of mid-face volume and cheekbone definition using fillers, silicone implants, or fat grafting to improve facial balance and reverse age-related volume loss.",
      procedureType: "Surgical or Non-Surgical",
      bodyLocation: { "@type": "AnatomicalStructure", name: "Cheek" },
      followup: "Fillers: immediate return to daily activities. Implants — Days 1–7: swelling, soft diet. Week 1–2: return to work. Month 1–3: final results visible.",
      howPerformed: "Fillers: injection under local anaesthetic, 20–30 minutes. Implants: general anesthesia; incision inside upper gum line; implant placed on cheekbone; 1 to 2 hours; same-day discharge.",
      url: "https://drvickyjain.com/cheek-augmentation-in-mumbai",
    },
    {
      "@type": "FAQPage",
      mainEntity: [
        { "@type": "Question", name: "What is the difference between cheek fillers and cheek implants?", acceptedAnswer: { "@type": "Answer", text: "Fillers add volume using injectable hyaluronic acid and last 9 to 18 months. Implants are surgically placed silicone structures that sit on the cheekbone permanently. Fillers suit mild cases and patients who want flexibility. Implants suit patients with flat bone structure who want a permanent result." } },
        { "@type": "Question", name: "Is cheek augmentation painful?", acceptedAnswer: { "@type": "Answer", text: "Filler injections involve minimal discomfort with topical numbing. Implant surgery is performed under general anesthesia, so there is no pain during the procedure. Post-operative discomfort from implant surgery is mild and managed with medication." } },
        { "@type": "Question", name: "How long do cheek filler results last?", acceptedAnswer: { "@type": "Answer", text: "Hyaluronic acid fillers in the cheeks typically last 9 to 18 months depending on the product used and how your body metabolizes it. Implants and fat grafting produce long-lasting to permanent results." } },
        { "@type": "Question", name: "Will cheek augmentation look natural?", acceptedAnswer: { "@type": "Answer", text: "Yes, when the volume or implant size is matched to your facial proportions. The most common cause of unnatural results is overcorrection: placing too much volume in the wrong anatomical plane." } },
        { "@type": "Question", name: "Can cheek augmentation reduce nasolabial folds?", acceptedAnswer: { "@type": "Answer", text: "Yes. Restoring mid-face volume lifts the tissue above the fold and reduces its depth indirectly. It is not a direct treatment for the fold itself, but mid-face support is one of the main reasons folds deepen with age." } },
        { "@type": "Question", name: "Can cheek augmentation be combined with other procedures?", acceptedAnswer: { "@type": "Answer", text: "Yes. It is commonly combined with chin augmentation for overall facial balance, rhinoplasty for complete facial harmony, and facelift or thread lift for patients with skin laxity alongside volume loss." } },
        { "@type": "Question", name: "Are there visible scars after cheek implant surgery?", acceptedAnswer: { "@type": "Answer", text: "No. The incision is made inside the mouth along the upper gum line. There are no external scars." } },
        { "@type": "Question", name: "Can cheek fillers be reversed?", acceptedAnswer: { "@type": "Answer", text: "Yes. Hyaluronic acid fillers can be dissolved with hyaluronidase at any point. Implants require a surgical procedure to remove. Fat grafting cannot be reversed once the fat has integrated." } },
      ],
    },
  ],
};

const CheekAugmentationPage = () => (
  <>
    <SEO
      title="Cheek Augmentation | Dr. Vicky Jain"
      description="Flat or hollow cheeks age the entire face. Dr. Vicky Jain performs cheek augmentation using fillers, implants, or fat grafting. Book your consultation."
      canonical="/cheek-augmentation-in-mumbai"
    />
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(cheekBreadcrumb) }} />
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(cheekStructuredData) }} />
    <Breadcrumb />
    <HeroSection />
    <WhatIsSection />
    <DrJainApproachSection />
    <CheekProblemsSection />
    <ProcedureTypesSection />
    <HowSurgeryPerformedSection />
    <RecoveryTimelineSection />
    <WhoIsGoodCandidateSection />
    <PatientResultsSection />
    <FAQSection />
    <ConsultationCTASection />
    <RelatedProceduresSection />
  </>
);

export default CheekAugmentationPage;
